import React, { useEffect, useRef, useState } from 'react';

import { EmployeeVoucher } from '../../../models/employee.model';
import { ContractDA } from '../../../services/admin/contract.service';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { MediaDA } from '../../../services/admin/media.service';
import { usePdfViewer } from '../../../contexts/PdfViewerContext';
import { attachmentPdfFilename } from '../../../scripts/document.filename.script';
import { datetimeFormat12h } from '../../../scripts/date.time.format.script';
import { modalShow, modalHide } from '../../../utils/modal.util';
import { GlobalSwal } from '../../../styles/swal.style';
import { getError } from '../../../languages/translations/response';

import AppModalAttachDocument from '../../../components/modal/ModalAttachDocument';

function roleLabel(lang: any, role?: string | null): string {
  const map: Record<string, string> = {
    employment_contract: lang.labels.roleEmploymentContract || 'Employment contract',
    amendment: lang.labels.roleAmendment || 'Amendment',
    identity: lang.labels.roleIdentity || 'Identity',
    residence_permit: lang.labels.roleResidencePermit || 'Residence permit',
    certificate: lang.labels.roleCertificate || 'Certificate',
    other: lang.labels.roleOther || 'Other',
  };
  return map[role ?? ''] ?? role ?? '-';
}

interface ModalDocumentManagerProps {
  modalId: string;
  title: string;
  companyId: string;
  entityType: 'contract' | 'employee';
  entityId: string;
  employeeId?: string;
  vouchers: EmployeeVoucher[];
  roles: readonly string[];
  lang: any;
  onChanged: () => void;
  canDelete?: (v: EmployeeVoucher) => boolean;
  showList?: boolean;
  autoOpen?: boolean;
}

const ModalDocumentManager: React.FC<ModalDocumentManagerProps> = ({
  modalId, title, companyId, entityType, entityId, employeeId, vouchers, roles, lang, onChanged, canDelete, showList = true, autoOpen = false,
}) => {
  const { openUrl } = usePdfViewer();
  const hiddenFileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(roles[0] as string ?? 'other');
  const [localVouchers, setLocalVouchers] = useState<EmployeeVoucher[]>(vouchers);
  const dirtyRef = useRef(false);

  const attachSubModalId = `${modalId}-browse`;

  useEffect(() => {
    if (autoOpen) {
      modalShow(modalId);
    }

    const element = document.getElementById(modalId);
    if (element) {
      const onHidden = () => {
        if (dirtyRef.current) {
          onChanged();
        }
      };
      element.addEventListener('hidden.bs.modal', onHidden);
      return () => element.removeEventListener('hidden.bs.modal', onHidden);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadVouchers = async () => {
    if (entityType === 'contract' && employeeId) {
      const res = await ContractDA.getContracts(companyId, employeeId);
      if (res.status === 200) {
        const contracts = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        const contract = contracts.find((c: any) => c.id === entityId);
        setLocalVouchers(contract?.vouchers ?? []);
      }
    } else {
      const res = await EmployeeDA.getEmployee(companyId, entityId);
      if (res.status === 200) {
        setLocalVouchers(res.data?.vouchers ?? []);
      }
    }
  };

  const handleSuccess = () => {
    dirtyRef.current = true;
    if (showList) {
      reloadVouchers();
    } else {
      modalHide(modalId);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(true);
    try {
      const res = entityType === 'contract'
        ? await ContractDA.uploadVoucher(companyId, entityId, file, selectedRole)
        : await EmployeeDA.uploadVoucher(companyId, entityId, file, selectedRole);
      if (res.status === 200 || res.status === 201) {
        handleSuccess();
      } else {
        GlobalSwal.fire({
          title: getError(res.data?.code, lang.code, res.data?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePick = async (idDocInbox: string) => {
    try {
      const body = { id_document_inbox: idDocInbox, role: selectedRole };
      const res = entityType === 'contract'
        ? await ContractDA.attachVoucher(companyId, entityId, body)
        : await EmployeeDA.attachVoucher(companyId, entityId, body);
      if (res.status === 200 || res.status === 201) {
        handleSuccess();
      } else {
        GlobalSwal.fire({
          title: getError(res.data?.code, lang.code, res.data?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch (err) {
      console.error('Attach failed', err);
    }
  };

  const handleDelete = async (v: EmployeeVoucher) => {
    if (!v.id_document_inbox) return;

    const result = await GlobalSwal.fire({
      text: lang.labels.confirmRemoveDocument || 'Remove this document?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm || 'Yes',
      cancelButtonText: lang.labels.noCancel || 'No',
    });
    if (!result.isConfirmed) return;

    try {
      const res = entityType === 'contract'
        ? await ContractDA.deleteVoucher(companyId, entityId, v.id_document_inbox)
        : await EmployeeDA.deleteVoucher(companyId, entityId, v.id_document_inbox);
      if (res.status === 200) {
        handleSuccess();
      } else {
        GlobalSwal.fire({
          title: getError(res.data?.code, lang.code, res.data?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleOpenDoc = async (v: EmployeeVoucher) => {
    if (!v.id_document_inbox) return;
    setLoadingDoc(v.id_document_inbox);
    try {
      const signedUrl = await MediaDA.getDocumentSignedUrl(companyId, v.id_document_inbox);
      if (signedUrl) {
        modalHide(modalId);
        openUrl(signedUrl, attachmentPdfFilename(v.original_name, 'document'));
      }
    } catch (err) {
      console.error('Failed to open document', err);
    } finally {
      setLoadingDoc(null);
    }
  };

  return (
    <div id={modalId} className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{title}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body">
            {showList ? (
              localVouchers.length === 0 ? (
                <p className="text-muted fw-bold text-center py-5">
                  {lang.labels.noDocumentsYet || 'No documents yet'}
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-0">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-200px">{lang.labels.name}</th>
                        <th className="min-w-100px">{lang.labels.role || 'Type'}</th>
                        <th className="min-w-100px">{lang.labels.date}</th>
                        <th className="w-50px text-end"></th>
                      </tr>
                    </thead>
                    <tbody className="text-secondary fw-bold">
                      {localVouchers.map((v, idx) => {
                        const docId = v.id_document_inbox ?? '';
                        const deletable = canDelete ? canDelete(v) : true;

                        return (
                          <tr key={`${docId}-${idx}`}>
                            <td>
                              <div className="d-flex align-items-center cursor-pointer" onClick={() => handleOpenDoc(v)}>
                                <i className="bi bi-file-earmark-pdf text-danger me-2 fs-4"></i>
                                <span className="fw-bolder text-hover-primary">
                                  {loadingDoc === docId
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : v.original_name ?? 'Document'
                                  }
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge badge-light-primary fs-8">
                                {roleLabel(lang, v.role)}
                              </span>
                            </td>
                            <td className="text-muted fs-7">
                              {v.attached_at ? datetimeFormat12h(lang.code, v.attached_at) : '-'}
                            </td>
                            <td className="text-end">
                              {deletable ? (
                                <button
                                  className="btn btn-sm btn-icon btn-light-danger"
                                  title={lang.labels.removeDocument || 'Remove'}
                                  onClick={() => handleDelete(v)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              ) : (
                                <i
                                  className="bi bi-lock text-muted"
                                  title={lang.labels.documentLinkedToContract || 'Linked to contract'}
                                ></i>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </div>
          <div className="modal-footer d-flex justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm w-auto"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r} value={r}>{roleLabel(lang, r)}</option>
                ))}
              </select>
              <button
                className="btn btn-sm btn-light-primary"
                type="button"
                onClick={() => modalShow(attachSubModalId)}
              >
                <i className="bi bi-paperclip me-1"></i>
                {lang.labels.attachExistingDocument || 'Attach existing'}
              </button>
              <button
                className="btn btn-sm btn-light-primary"
                type="button"
                disabled={uploading}
                onClick={() => hiddenFileRef.current?.click()}
              >
                {uploading
                  ? <span className="spinner-border spinner-border-sm me-1"></span>
                  : <i className="bi bi-cloud-upload me-1"></i>
                }
                {lang.labels.uploadNewPdf || 'Upload'}
              </button>
              <input
                ref={hiddenFileRef}
                type="file"
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={handleUpload}
              />
            </div>
            <button className="btn btn-light" type="button" data-bs-dismiss="modal">
              {lang.labels.close || 'Close'}
            </button>
          </div>
        </div>
      </div>
      <AppModalAttachDocument
        id_company={companyId}
        entity_type={entityType}
        onAttached={() => {}}
        onPick={(idDoc) => handlePick(idDoc)}
        modalId={attachSubModalId}
      />
    </div>
  );
};

export default ModalDocumentManager;
